/* Foreground browser/desktop notifications (Notification API) — NOT true
   background Web Push. This only works while a SalesOS tab is open in the
   browser; nothing is delivered if the browser/tab is fully closed. True
   background push would need a push subscription (VAPID keys + a stored
   per-device subscription + a backend push-send endpoint using the
   service worker's existing `push` event) — none of that exists yet, and
   is out of scope for this pass. State that honestly wherever this is
   surfaced; never imply background delivery.

   Never auto-prompts: permission is requested ONLY from
   requestBrowserNotificationPermission(), which must be called from a
   real user click (a browser's own requirement anyway — a permission
   prompt triggered without a user gesture is silently ignored or
   auto-denied by most browsers). Page load never calls this. */

const ENABLED_KEY = "salesos.browserNotifications.enabled";

// Notification `kind` values (see core.notify's `kind` param — task_overdue,
// "assignment", "urgent", plus anything explicitly needing action) that are
// worth a browser notification. Everything else (routine "info") only ever
// shows in the in-app bell — never spams the OS notification tray.
const IMPORTANT_KINDS = new Set(["task_overdue", "assignment", "urgent", "approval"]);

export function isNotificationSupported() {
  return typeof window !== "undefined" && "Notification" in window;
}

/** "unsupported" | "not_enabled" | "enabled" | "blocked" */
export function getBrowserNotificationState() {
  if (!isNotificationSupported()) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  if (Notification.permission === "granted" && localStorage.getItem(ENABLED_KEY) === "true") {
    return "enabled";
  }
  return "not_enabled";
}

/** Must be called synchronously from a user click handler — never on page
    load / login / any automatic trigger. */
export async function requestBrowserNotificationPermission() {
  if (!isNotificationSupported()) return "unsupported";
  try {
    const result = await Notification.requestPermission();
    if (result === "granted") {
      localStorage.setItem(ENABLED_KEY, "true");
    }
    return result; // "granted" | "denied" | "default"
  } catch {
    return "denied";
  }
}

export function disableBrowserNotifications() {
  localStorage.setItem(ENABLED_KEY, "false");
}

export function isNotificationWorthShowing(n) {
  // Approval-requested notifications currently ship with kind="info" (the
  // notify()/notify_role() default) rather than a distinct kind — matched
  // here by their link instead, so "Approval requiring action" is covered
  // without requiring an unrelated backend kind change just for this.
  return IMPORTANT_KINDS.has(n.kind) || n.related?.link === "/approvals";
}

/** Shows a real OS/browser notification for one SalesOS notification.
    No-op if not supported/enabled/permitted — callers don't need to
    re-check state themselves. `onOpen` is called (with the SalesOS
    notification) if the user clicks it. */
export function showBrowserNotification(n, onOpen) {
  if (getBrowserNotificationState() !== "enabled") return;
  if (!isNotificationWorthShowing(n)) return;
  try {
    const browserNotif = new Notification(n.title, {
      body: n.body, tag: n.notification_id, // same tag = de-dupes if shown twice
    });
    browserNotif.onclick = () => {
      window.focus();
      onOpen?.(n);
      browserNotif.close();
    };
  } catch {
    // Never let a notification failure break the app.
  }
}
